import React, { useEffect, useState } from 'react';

import { Card, EmptyState } from '@copilot/ui';
import * as adminApi from '../../services/adminApi';
import { formatAdminDate } from './adminFormatting';

// What each ledger row means, in support's words.
const ENTRY_LABELS = {
  grant: 'Granted',
  consume: 'Used',
  restore: 'Given back',
  refund: 'Refunded',
  adjust: 'Adjusted',
};

const SOURCE_LABELS = {
  free_trial: 'Free trial',
  subscription: 'Plan allowance',
  meeting_pass: 'Meeting Pass',
  signup_trial: 'Signup trial (one-time)',
};

function describe(entry) {
  const source = SOURCE_LABELS[entry.source_type] || entry.source_type;
  const purpose = entry.details?.purpose === 'extra_meeting' ? ' (extra meeting)' : '';
  const reason = entry.details?.reason === 'short_session' ? ' - accidental start' : '';
  return `${ENTRY_LABELS[entry.entry_type] || entry.entry_type} · ${source}${purpose}${reason}`;
}

// The user's meeting credit ledger (GET /api/admin/users/{id}/credit-
// ledger): where every meeting they could start came from and where it
// went. Read-only, and best-effort - the rest of the page never depends
// on it.
function CreditLedgerCard({ userId }) {
  const [state, setState] = useState({ status: 'loading', items: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    // Wrapped so even a call that throws before returning a promise only
    // marks this card as failed.
    Promise.resolve()
      .then(() => adminApi.getUserCreditLedger(userId, { limit: 20 }))
      .then((page) => {
        if (!cancelled) {
          setState({ status: 'loaded', items: page?.items || [], total: page?.total || 0 });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', items: [], total: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <Card interactive title="Meeting credits" className="admin-section-heading">
      {state.status === 'loading' && <p className="admin-recent-meeting-meta">Loading…</p>}
      {state.status === 'error' && (
        <p className="admin-recent-meeting-meta">The credit history could not be loaded.</p>
      )}
      {state.status === 'loaded' && state.items.length === 0 && (
        <EmptyState title="No credit history yet." description="Grants and meetings appear here from now on." />
      )}
      {state.status === 'loaded' && state.items.length > 0 && (
        <div className="admin-recent-meetings">
          {state.items.map((entry) => (
            <div className="admin-recent-meeting-row" key={entry.id}>
              <span>
                <span className="admin-recent-meeting-title">{describe(entry)}</span>
                <span className="admin-recent-meeting-meta">
                  {formatAdminDate(entry.created_at)}
                  {entry.expires_at ? ` · expires ${formatAdminDate(entry.expires_at)}` : ''}
                </span>
              </span>
              <strong>{entry.credits_delta > 0 ? `+${entry.credits_delta}` : entry.credits_delta}</strong>
            </div>
          ))}
          {state.total > state.items.length && (
            <p className="admin-recent-meeting-meta">
              Showing the latest {state.items.length} of {state.total}.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

export default CreditLedgerCard;
